import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { useInstantSearch } from "react-instantsearch";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { create } from "zustand";
import { getCsrfToken } from "../state/csrf";
import { AutoGrowTextarea } from "./AutoGrowTextarea";
import { parseFilterTag, stripFilterTag, toIndexUiState } from "./filters";
import { markdownComponents } from "./Markdown";
import { readSse } from "./sse";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

interface KavelChatState {
  messages: ChatMessage[];
  isStreaming: boolean;
  pendingFilters: Record<string, any> | null;
  sendMessage: (content: string) => Promise<void>;
  clearChat: () => void;
  consumeFilters: () => Record<string, any> | null;
}

export const useKavelChat = create<KavelChatState>((set, get) => ({
  messages: [],
  isStreaming: false,
  pendingFilters: null,
  clearChat: () => set({ messages: [], isStreaming: false, pendingFilters: null }),
  consumeFilters: () => {
    const filters = get().pendingFilters;
    if (filters) set({ pendingFilters: null });
    return filters;
  },
  sendMessage: async (content: string) => {
    const userMessage: ChatMessage = { role: "user", content };
    const history = [...get().messages, userMessage];
    set({
      messages: [...history, { role: "assistant", content: "" }],
      isStreaming: true,
    });

    let fullResponse = "";

    try {
      const response = await fetch("/api/kavel/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": getCsrfToken(),
        },
        body: JSON.stringify({ messages: history }),
      });
      await readSse(response, (data) => {
        if (!data.content) return;
        fullResponse += data.content;
        const displayText = stripFilterTag(fullResponse);
        set((state) => {
          const messages = [...state.messages];
          const idx = messages.length - 1;
          if (idx >= 0 && messages[idx].role === "assistant") {
            messages[idx] = { ...messages[idx], content: displayText };
          }
          return { messages };
        });
      });

      const filters = parseFilterTag(fullResponse);
      if (filters) {
        set({ pendingFilters: filters });
      }
    } catch {
      set((state) => {
        const messages = [...state.messages];
        const fallback = "Er ging iets mis tijdens chatten. Probeer het later opnieuw.";
        const idx = messages.length - 1;
        if (idx >= 0 && messages[idx].role === "assistant" && !messages[idx].content) {
          messages[idx] = { ...messages[idx], content: fallback };
          return { messages };
        }
        return { messages: [...messages, { role: "assistant", content: fallback }] };
      });
    } finally {
      set({ isStreaming: false });
    }
  },
}));

function useApplyFilters() {
  const { setIndexUiState } = useInstantSearch();
  const setStateRef = useRef(setIndexUiState);

  useEffect(() => {
    setStateRef.current = setIndexUiState;
  });

  const pendingFilters = useKavelChat((s) => s.pendingFilters);
  const consumeFilters = useKavelChat((s) => s.consumeFilters);

  useEffect(() => {
    const filters = consumeFilters();
    if (!filters || Object.keys(filters).length === 0) return;
    const state = toIndexUiState(filters);
    setStateRef.current(state);
  }, [pendingFilters, consumeFilters]);
}

export const KavelChat = () => {
  const { messages, isStreaming, sendMessage } = useKavelChat();
  const [input, setInput] = useState("");
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const trimmed = useMemo(() => input.trim(), [input]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isStreaming]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!trimmed || isStreaming) return;
    setInput("");
    await sendMessage(trimmed);
  };

  useApplyFilters();

  return (
    <div className="flex flex-col flex-1 min-h-0 p-2">
      <div className="bg-gray-50 border border-gray-200 rounded p-2 flex-1 min-h-0 overflow-y-auto space-y-2">
        {messages.length === 0 ? (
          <p
            className="text-xs text-gray-400 cursor-text"
            onClick={() => inputRef.current?.focus()}
          >
            Beschrijf wat voor kavels je zoekt, bijvoorbeeld &quot;Bedrijfbestemming in Groningen
            binnen de bebouwde kom&quot;.
          </p>
        ) : (
          messages.map((message, idx) => (
            <div key={idx}>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">
                {message.role === "user" ? "Jij" : "Assistent"}
              </p>
              <div className="text-xs text-black wrap-break-word">
                {message.role === "user" ? (
                  <p className="whitespace-pre-wrap">{message.content}</p>
                ) : (
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {message.content}
                  </ReactMarkdown>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={onSubmit} className="flex items-end gap-1 shrink-0 mt-2">
        <AutoGrowTextarea
          ref={inputRef}
          className="flex-1 border border-gray-200 rounded px-2 py-1 text-xs"
          placeholder="Beschrijf je zoekcriteria..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={isStreaming}
        />
        <button
          type="submit"
          className="px-2 py-1 bg-bright-yellow hover:bg-yellow rounded text-xs disabled:opacity-50"
          disabled={!trimmed || isStreaming}
        >
          {isStreaming ? "..." : "Zoek"}
        </button>
      </form>
    </div>
  );
};
